import { ChevronDown } from "baseui/icon";
import classNames from "classnames";
import moment from "moment";
import React, { FC, useEffect, useState } from "react";
import { HiPlus } from "react-icons/hi2";
import { LuChevronRight } from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";

import {
  AvatarStack,
  AvatarStackItem,
} from "../../../../../../../../components/avatar/AvatarStack";
import { Card } from "../../../../../../../../components/card/Card";
import { LottieLoading } from "../../../../../../../../components/graphics/LottieLoading";
import { Button } from "../../../../../../../../components/newUI/button";
import ContactWorkSvg from "../../../../../../../../components/svg/ContactWorkSvg";
import {
  ARCHIVED_CLIENT_TIP,
  getInitialsFromFullName,
  inactiveStatusId,
  showStatus,
} from "../../../../../../../../constants/constants";
import { WorkModel } from "../../../../../../../../factories/works/models/work.model";
import { useAppSelector } from "../../../../../../../../redux/redux";
import { getLinkedFirm } from "../../../../../../../../sessionStorage/sessionStorage";
import { useGetWorksQuery } from "../../../../../../../../slices/WorkSlice";
import { IOrganizationContact } from "../../../../../../../../types/types";
import { StatusesDropdown } from "../../../../../../../works/dropdown/StatusesDropdown";
import { getStatusColor } from "../../../../../../../works/dropdown/utils";
import { AddWorkModal } from "../../../../../../../works/modals/AddWork.modal";
import { WorkTableSort } from "../../../../../../../works/types/work-table";
import { ProfileWidgetEmptyState } from "../../../../../../components/profile";
import ErrorPopup from "../../../../../../modals/ErrorPopup";

type Props = {
  organization: IOrganizationContact;
};

const mapAssigneesToAvatarItems = (
  assignees: WorkModel["assigneeList"],
): AvatarStackItem[] =>
  assignees.map((assignee) => {
    const name = [assignee.firstName, assignee.middleName, assignee.lastName]
      .filter(Boolean)
      .join(" ");

    return {
      id: assignee.assignedUserId,
      label:
        `${assignee.firstName?.[0] || ""}${
          assignee.lastName?.[0] || ""
        }`.toUpperCase() || "?",
      name,
      color: assignee.color,
    };
  });

export const OrganizationWorksWidget: FC<Props> = ({ organization }) => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.appReducer);

  const [page, setPage] = useState(1);

  const {
    data: contactWorks,
    isFetching,
    isLoading,
  } = useGetWorksQuery({
    pageNumber: page,
    pageSize: 20,
    sortBy: WorkTableSort.StartDate,
    sortOrder: "desc",

    orgId: getLinkedFirm()?.orgId,
    customerIds: [`${organization?.customerId}`],
    currentUserId: user.id,
    hideGreenStatus: false,
  });

  const [works, setWorks] = useState<WorkModel[]>([]);

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenError, setIsOpenError] = useState(false);

  const isActive = organization?.contactStatusEnumId !== inactiveStatusId;

  const isOverdue = (work: WorkModel) => {
    return moment().startOf("day").unix() > moment(work.dueDate).unix();
  };

  const hasPages = (contactWorks?.[0]?.totalRecords || 0) > page * 20;

  useEffect(() => {
    if (contactWorks?.length) {
      setWorks((currentWorks) => {
        const existingIds = new Set(currentWorks.map((work) => work.workId));
        const newWorks = contactWorks.filter(
          (work) => !existingIds.has(work.workId),
        );
        return [...currentWorks, ...newWorks];
      });
    }
  }, [contactWorks]);

  return (
    <>
      <Card extraClasses={"overflow-hidden"}>
        <div className={"flex h-[386px] max-w-full flex-col p-[22px]"}>
          <div
            className={"mb-4 flex shrink-0 items-center justify-between gap-4"}>
            <div className={"text-[19px] font-extrabold text-[#1F2433]"}>
              Work
            </div>
            <div className={"flex shrink-0 items-center gap-3"}>
              <Button
                type={"button"}
                variant={"ghost"}
                size={"sm"}
                autoWidth
                rightIcon={<LuChevronRight size={14} strokeWidth={2.2} />}
                className={
                  "h-auto p-0 text-[13px] font-bold text-[#6B4EFF] hover:bg-transparent"
                }
                onClick={() =>
                  navigate(`/organization/${organization?.id}/works`)
                }>
                View all
              </Button>
              <span title={isActive ? "Add work" : ARCHIVED_CLIENT_TIP}>
                <Button
                  type={"button"}
                  variant={"outline"}
                  size={"sm"}
                  iconOnly
                  autoWidth
                  leftIcon={<HiPlus size={16} />}
                  className={
                    "h-8 w-8 rounded-lg border-[#E4E7EC] p-0 text-[#6B4EFF] hover:bg-[#F4F2FF]"
                  }
                  disabled={!isActive}
                  aria-label={isActive ? "Add work" : ARCHIVED_CLIENT_TIP}
                  onClick={() => isActive && setIsOpenAdd(true)}
                />
              </span>
            </div>
          </div>
          <div className={"flex min-h-0 flex-1 flex-col"}>
            {isLoading ? (
              <div className={"p-4"}>
                <LottieLoading />
              </div>
            ) : !works?.length ? (
              <ProfileWidgetEmptyState
                icon={<ContactWorkSvg />}
                title={"No work items"}
                description={
                  "Work added for this organization will appear here."
                }
              />
            ) : (
              <>
                <div
                  className={
                    "grid grid-cols-[minmax(0,1.4fr)_minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,0.7fr)] border-b border-[#EEF0F3] pb-2.5 text-[12px] font-bold text-[#9097A8]"
                  }>
                  <span>Work name</span>
                  <span>Status</span>
                  <span>Due date</span>
                  <span>Assignee</span>
                </div>
                <div
                  className={
                    "mr-[-22px] min-h-0 flex-1 overflow-y-auto pr-[22px]"
                  }>
                  {works?.map((work) => (
                    <div
                      key={work?.workId}
                      className={
                        "grid grid-cols-[minmax(0,1.4fr)_minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(0,0.7fr)] items-center border-b border-[#F4F5F7] py-[13px]"
                      }>
                      <Link
                        to={`/work/${work?.workId}`}
                        className={
                          "min-w-0 truncate pr-2 text-left text-[13.5px] font-bold text-[#6B4EFF] hover:underline"
                        }>
                        {work?.workName}
                      </Link>
                      <div
                        className={"min-w-0"}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}>
                        {work?.jobStatus && (
                          <StatusesDropdown
                            workTypeId={work?.workTypeId || ""}
                            workId={work?.workId || ""}
                            triggerClassName={"max-w-full min-w-0"}
                            triggerElement={
                              <div
                                className={
                                  "flex min-w-0 max-w-full cursor-pointer items-center gap-1.5 overflow-hidden rounded-[7px] border border-[#E4E7EC] bg-white px-2.5 py-1 text-[12.5px] font-semibold text-[#1F2433]"
                                }>
                                <span
                                  className={"h-2 w-2 shrink-0 rounded-full"}
                                  style={{
                                    backgroundColor: getStatusColor(
                                      work?.jobStatus || "",
                                    ),
                                  }}
                                />
                                <span className={"min-w-0 truncate"}>
                                  {showStatus(work)}
                                </span>
                                <ChevronDown
                                  size={12}
                                  color={"#9097A8"}
                                  className={"shrink-0"}
                                />
                              </div>
                            }
                          />
                        )}
                      </div>
                      <div
                        className={classNames(
                          "whitespace-nowrap pl-2 text-[13px] font-semibold",
                          isOverdue(work) ? "text-[#E5484D]" : "text-[#232A38]",
                        )}>
                        {moment(work?.dueDate).format("MMM DD, YYYY")}
                      </div>
                      <div className={"flex min-w-0 items-center"}>
                        <AvatarStack
                          items={mapAssigneesToAvatarItems(
                            work.assigneeList || [],
                          )}
                          maxVisible={3}
                          withPreview
                          previewTitle={"Assignees:"}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {hasPages && (
                  <div className={"pt-3"}>
                    <Button
                      type={"button"}
                      variant={"outline"}
                      size={"sm"}
                      fullWidth
                      onClick={() => setPage((prev) => prev + 1)}
                      disabled={isFetching}
                      loading={isFetching}>
                      Load more
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Card>
      {isOpenAdd && (
        <AddWorkModal
          isOpen={isOpenAdd}
          onClose={() => setIsOpenAdd(false)}
          defaultContact={{
            value: organization?.customerId || "",
            label: `${organization?.companyName}`,
            avatar: getInitialsFromFullName(organization?.companyName),
            avatarColor: "purple",
          }}
        />
      )}
      <ErrorPopup
        closePopup={() => setIsOpenError(false)}
        isOpen={isOpenError}
      />
    </>
  );
};
